import { CLIENT_ACTIVE_STATUS } from '../interface/client';
import { ControlMessageType } from '../interface/enum-controlMessages-type';
import {
  WhatsappPhoneCandidates,
  WhatsappPhoneSource,
  WhatsappPlaceholderInput,
  WhatsappPlaceholderOs,
  WhatsappPlaceholders
} from '../interface/whatsapp-message';
import { ClientUtilities } from './client-utilities';
import { Utilities } from './utilities';

const LABEL_TO_TYPE = new Map<string, ControlMessageType>([
  ['ABRIR_ORÇAMENTO', ControlMessageType.AbrirOrcamento],
  ['ABRIR_OS', ControlMessageType.AbrirOS],
  ['FECHAR_OS', ControlMessageType.FecharOS],
  ['SOLICITAR_AUTORIZACAO', ControlMessageType.SolicitacaoAtualizacao],
  ['POS_VENDA', ControlMessageType.PosVenda],
  ['ENVIAR_PESQUISA', ControlMessageType.PesquisaDeSatisfacao],
  ['ANIVERSARIANTE', ControlMessageType.Aniversariante]
]);

const PLACEHOLDER_PATTERN = /{{\s*([^{}\s]+)\s*}}/g;
const LOCAL_PHONE_LENGTHS = [10, 11];
const INTERNATIONAL_PHONE_LENGTHS = [12, 13];
const BRAZIL_COUNTRY_CODE = '55';
const BUDGET_STATE_GROUPS = ['budget', 'rejected'];

export class WhatsappMessageUtilities extends Utilities {

  static labelToType(label: string): ControlMessageType | null {
    return LABEL_TO_TYPE.get(label) ?? null;
  }

  static replacePlaceholders(template: string, values: Partial<Record<string, string>>): string {
    return (template ?? '').replace(PLACEHOLDER_PATTERN, (match, key: string) => {
      const value = Object.prototype.hasOwnProperty.call(values, key) ? values[key] : undefined;
      return value ?? match;
    });
  }

  static buildPlaceholders({ ro, company, consultorName }: WhatsappPlaceholderInput): WhatsappPlaceholders {
    const client = this._text(ro?.clientTmp || ro?.client?.name);

    return {
      company: this._text(company?.fancyName || company?.socialName),
      client,
      nome: client,
      plate: this._text(ro?.vehicleTmp || ro?.vehicle?.plate),
      budget: this._text(ro?.budgetCode),
      os: this._text(this._osCode(ro)),
      consultor: this._text(consultorName),
      link: this._text(ro?.shortUrl)
    };
  }

  static normalizePhone(raw: string | null | undefined): string | null {
    const digits = this.removeMask(raw ?? '');

    if (LOCAL_PHONE_LENGTHS.includes(digits.length)) {
      return `${BRAZIL_COUNTRY_CODE}${digits}`;
    }

    if (INTERNATIONAL_PHONE_LENGTHS.includes(digits.length)) {
      return digits;
    }

    return null;
  }

  static resolvePhoneCandidates(ro: WhatsappPhoneSource | null | undefined): WhatsappPhoneCandidates {
    const client = ro?.client;

    if (!client) {
      return { skip: 'missing_data' };
    }

    if (ClientUtilities.isDefault(client.document)) {
      const telefTmp = ro?.telefTmp?.trim();
      return telefTmp ? { phone: telefTmp } : { skip: 'missing_data' };
    }

    if (client.active === CLIENT_ACTIVE_STATUS.INACTIVE) {
      return { skip: 'client_inactive' };
    }

    const whatsapp = client.whatsapp?.trim();
    if (whatsapp) {
      return { phone: whatsapp };
    }

    const phone1 = client.phone1?.trim() ?? '';
    const phone2 = client.phone2?.trim() ?? '';
    if (phone1 && phone2) {
      return { choose: [phone1, phone2] };
    }

    return { phone: phone1 };
  }

  private static _osCode(ro: WhatsappPlaceholderOs | null | undefined): number | undefined {
    if (ro?.relativeCode !== undefined && ro?.relativeCode !== null) {
      return ro.relativeCode;
    }

    const isBudget = ro?.isBudgetOrRejected ?? BUDGET_STATE_GROUPS.includes(ro?.stateGroup ?? '');
    return isBudget ? ro?.budgetCode : ro?.codeSystem ?? ro?.budgetCode;
  }

  private static _text(value: string | number | null | undefined): string {
    return value?.toString() ?? '';
  }
}
